import { useEffect, useMemo, useState } from 'react';

import type { ItemChangedReason, TreeItems } from '@/components/ui/sortable-tree';
import { flattenTree } from '@/components/ui/sortable-tree';
import { useNestedPageViewStore } from '@/stores/nested-page-view-store';
import type { Page } from '@/types/page';
import { PageType } from '@/types/page';
import type { ReorderOperation } from './page-utils';
import { isValidPinnedBoundaryDrop, pagesToTreeItems } from './page-utils';
import type { SortablePageItemData } from './sortable-page-item';

// Page types that support full drag-and-drop reordering (all items) in the sidebar
const FULL_REORDERABLE_TYPES = new Set<PageType>([PageType.CASE_RESULT]);

// Page types that support drag-and-drop reordering only for pinned items
const PINNED_ONLY_REORDERABLE_TYPES = new Set<PageType>([PageType.BLOG]);
const EMPTY_PAGES: Page[] = [];

export type ReorderMode = 'all' | 'pinned-only' | 'none';

export function resolveReorderMode(pageType: PageType): ReorderMode {
  if (FULL_REORDERABLE_TYPES.has(pageType)) return 'all';
  if (PINNED_ONLY_REORDERABLE_TYPES.has(pageType)) return 'pinned-only';
  return 'none';
}

interface UsePageTypeReorderParams {
  pages: Page[];
  reorderMode: ReorderMode;
  maxPinnedPages: number | null | undefined;
  onReorder?: (operation: ReorderOperation) => void;
}

export function usePageTypeReorder({
  pages,
  reorderMode,
  maxPinnedPages,
  onReorder,
}: UsePageTypeReorderParams) {
  const pinnedPages = useMemo(() => pages.filter((p) => p.pinned), [pages]);
  const unpinnedPages = useMemo(() => pages.filter((p) => !p.pinned), [pages]);
  const pinLimitReached = maxPinnedPages != null && pinnedPages.length >= maxPinnedPages;

  const reorderablePages = useMemo(() => {
    if (reorderMode === 'all') return pages;
    if (reorderMode === 'pinned-only') return pinnedPages;
    return EMPTY_PAGES;
  }, [pages, pinnedPages, reorderMode]);
  const treeItems = useMemo(
    () => pagesToTreeItems(reorderablePages, { canPin: !pinLimitReached }),
    [reorderablePages, pinLimitReached],
  );
  const [localTreeItems, setLocalTreeItems] = useState<TreeItems<SortablePageItemData>>(treeItems);

  useEffect(() => {
    setLocalTreeItems(treeItems);
  }, [treeItems]);

  const handleItemsChanged = (
    newItems: TreeItems<SortablePageItemData>,
    reason: ItemChangedReason<SortablePageItemData>,
  ) => {
    if (reason.type !== 'dropped' || !onReorder) {
      setLocalTreeItems(newItems);
      return;
    }

    const { draggedItem } = reason;
    const flatItems = flattenTree(newItems);
    const draggedPageId = String(draggedItem.id);
    // All items are root-level (flat list, no nesting)
    const targetIndex = flatItems.findIndex((item) => String(item.id) === draggedPageId);

    // Enforce pinned/unpinned boundary: pinned items must stay before unpinned items
    const isDraggedPinned = draggedItem.page.pinned;
    if (!isValidPinnedBoundaryDrop(flatItems, targetIndex, isDraggedPinned)) {
      setLocalTreeItems(localTreeItems);
      return;
    }

    setLocalTreeItems(newItems);

    // Only consider siblings with the same pinned state to stay within the correct scope
    const prevSamePinned = flatItems
      .slice(0, targetIndex)
      .reverse()
      .find((item) => item.page.pinned === isDraggedPinned);
    const previousSiblingId = prevSamePinned ? String(prevSamePinned.id) : null;

    // For pinned-only reorderable types (blogs), include unpinned IDs after the reordered pinned ones
    // so they aren't dropped from the store's children list
    const reorderedIds = flatItems.map((item) => String(item.id));
    const unpinnedIds =
      reorderMode === 'pinned-only'
        ? unpinnedPages.map((p) => p.id).filter((id): id is string => id !== undefined)
        : [];
    useNestedPageViewStore
      .getState()
      .reorderActiveNestedChildren([...reorderedIds, ...unpinnedIds]);

    onReorder({
      pageId: draggedPageId,
      parentId: null,
      previousSiblingId,
    });
  };

  return { localTreeItems, handleItemsChanged, pinnedPages, unpinnedPages, pinLimitReached };
}
